import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME, HELPER_OPTION_MAX_ROWS } from '@/utils/consts'
import { useFetchAllRows, UseFetchAllRowsResult } from '@/hooks/useFetchAllRows'
import { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { getShipperListV2 } from '@/services/shipper/apis'
import { getBrandNameOptions, getGoodsCodeOptions, getStockLedgers, getWarehouseNameCombo } from './apis'
import { STOCK_LEDGER_MAX_PAGE_SIZE } from './payload'
import { GetBrandNameOptionsReq, GetGoodsCodeOptionsReq, GoodsCodeOption, StockLedgerItem, StockLedgerView } from './types'

const PATH = ['inventory', 'stockLedger'] as const

export const stockLedgerQueryKeys = {
  all: PATH,
  // 맨 앞 메뉴코드 — 탭을 닫으면 MenuOnSessionWrap.remove 가 이 키로 전량 캐시를 지운다. 쪽(page)은 key 가 아니다.
  list: (view: StockLedgerView | null) => ['st10', ...PATH, 'LIST', view && { filter: view.filter, seq: view.seq }] as const,
  warehouseNames: [...PATH, 'WAREHOUSE_NAMES'] as const,
  shippers: [...PATH, 'SHIPPERS'] as const,
  brandNames: (params: GetBrandNameOptionsReq) => [...PATH, 'BRAND_NAMES', params] as const,
  goodsCodes: (params: GetGoodsCodeOptionsReq) => [...PATH, 'GOODS_CODES', params] as const,
}

/**
 * 훅 반환 타입 — `data` 를 풀어 쓰는 대신 나머지 쿼리 상태(isFetching·refetch 등)는 그대로 넘긴다.
 * 반환 타입을 명시해야 소비 측이 추론에 기대지 않는다(CLAUDE.md React Query 훅 규칙).
 */
type QueryStateOf<TData> = Omit<UseQueryResult<TData, Error>, 'data'>

type OptionsResult = QueryStateOf<SelectOptionType<string>[]> & { options: SelectOptionType<string>[] }
type GoodsCodeOptionsResult = QueryStateOf<GoodsCodeOption[]> & { items: GoodsCodeOption[] }
interface BrandNameOptionsData {
  items: string[]
  /** 응답이 BE 상한(1,000행)에 닿았다 — 화면이 "조건을 좁혀 달라" 안내를 띄운다. */
  isTruncated: boolean
}
type BrandNameOptionsResult = QueryStateOf<BrandNameOptionsData> & BrandNameOptionsData

/**
 * ST03 재고수불 목록 — 1.0 방식 전량 조회(LOGISPRD-15270). BE 한 페이지 상한(`STOCK_LEDGER_MAX_PAGE_SIZE`)씩 이어 받는다.
 *
 * `view`(조건·조회 순번)가 있으면 받는다. 필수 필터(화주·마감일자)를 채우고 [조회]를 눌러야 `view` 가 생기므로 진입만으로는 조회하지 않는다.
 * 보는 쪽(`view.page`)은 key 에 넣지 않는다 — 쪽은 화면이 받은 행을 잘라 보여 준다. 다시 받는 것은 [조회](순번이 바뀜)뿐이다.
 * 실패 알럿은 훅의 전역 알럿(meta.errorToast)이 띄운다.
 */
export const useGetStockLedgers = (view: StockLedgerView | null): UseFetchAllRowsResult<StockLedgerItem> => {
  const filter = view?.filter
  return useFetchAllRows({
    queryKey: stockLedgerQueryKeys.list(view),
    fetchPage: async (page, size) => {
      if (!filter) throw new Error('재고수불 조회 조건이 없습니다.')
      // 0-base 구간 번호 → BE 1-base page
      const res = await getStockLedgers(filter, page + 1, size)
      const items = res.rtnData ?? []
      return { items, totalCount: Number(res.params?.totalCnt ?? items.length) }
    },
    chunkSize: STOCK_LEDGER_MAX_PAGE_SIZE,
    getKey: stockLedgerRowKey,
    enabled: view != null,
  })
}

/** 집계 그룹 키(화주 × 브랜드 × 상품코드 × 바코드 × 상품군) + SKU ID — 구간 경계에서 겹친 행을 거른다. null 과 '' 를 가른다. */
const stockLedgerRowKey = (r: StockLedgerItem): string => JSON.stringify([r.SPR_NM, r.BRD_NM, r.GDS_CD, r.BARCODE, r.GDS_GROUP1, r.SKU_ID])

/** 센터명 콤보 옵션. 변경 빈도가 낮아 공용 캐시 시간을 그대로 쓴다. */
export const useGetWarehouseNameOptions = (): OptionsResult => {
  const { data, ...rest } = useQuery({
    queryKey: stockLedgerQueryKeys.warehouseNames,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      const res = await getWarehouseNameCombo()
      // 콤보는 코드/명 배열이 짝을 이룬다. 명(COMBO_OPT_NM)이 비면 코드로 대체한다.
      return (res?.WH_NM?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: res?.WH_NM?.COMBO_OPT_NM?.[idx] || code,
      }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

/**
 * 화주명 셀렉트 옵션 — `POST /api/v1/get-shippers` 전체 목록(코드/명).
 *
 * ST03 필터 `SPR_NM` 은 화주**코드**라 value=코드, label=화주명으로 만든다.
 * (get-shipper-names 는 명(名)만 반환해 코드 필터에 쓸 수 없다 — ST02 와 다른 지점.)
 */
export const useGetShipperOptions = (): OptionsResult => {
  const { data, ...rest } = useQuery({
    queryKey: stockLedgerQueryKeys.shippers,
    queryFn: async (): Promise<SelectOptionType<string>[]> => {
      // 클라이언트 검색(showSearch) 전제로 전건을 한 번에 받는다(get-shipper-names 의 size=10000 선례).
      const res = await getShipperListV2({ shipperNames: [], logisticsServiceTypes: [], page: 0, size: 10000 })
      return (res?.data?.items ?? []).map((s) => ({ value: s.shipperCode, label: s.shipperName || s.shipperCode }))
    },
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { options: data ?? [], ...rest }
}

/**
 * 상품코드 서치헬퍼 목록. 조회 조건(화주·브랜드명·상품명)별로 캐시한다.
 * 모달이 닫혀 있는 동안 미리 조회하지 않도록 `enabled`(모달 open)로 게이트한다.
 */
export const useGetGoodsCodeOptions = (params: GetGoodsCodeOptionsReq, enabled = true): GoodsCodeOptionsResult => {
  const { data, ...rest } = useQuery({
    queryKey: stockLedgerQueryKeys.goodsCodes(params),
    queryFn: async () => {
      const res = await getGoodsCodeOptions(params)
      return res.data?.items ?? []
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data ?? [], ...rest }
}

/**
 * 브랜드명 서치헬퍼 목록. 조회 조건(화주·브랜드명)별로 캐시한다.
 * 모달이 닫혀 있는 동안 미리 조회하지 않도록 `enabled`(모달 open)로 게이트한다.
 *
 * 브랜드명이 NULL 인 상품도 distinct 1행으로 오므로 거른다. 잘림 판정은 거르기 전 건수로 한다.
 */
export const useGetBrandNameOptions = (params: GetBrandNameOptionsReq, enabled = true): BrandNameOptionsResult => {
  const { data, ...rest } = useQuery({
    queryKey: stockLedgerQueryKeys.brandNames(params),
    queryFn: async (): Promise<BrandNameOptionsData> => {
      const rows = (await getBrandNameOptions(params)).data?.items ?? []
      return { items: rows.map((row) => row.brandName ?? '').filter(Boolean), isTruncated: rows.length >= HELPER_OPTION_MAX_ROWS }
    },
    enabled,
    staleTime: API_COMMON_CACHE_TIME,
  })

  return { items: data?.items ?? [], isTruncated: data?.isTruncated ?? false, ...rest }
}
